/**
 * ============================================================================
 * BankAccountCard - BANK DETAILS AND SETTINGS DISPLAY
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. TWO STATES
 *    - Has bank details: Green "Active" badge, show details
 *    - No bank details: Yellow "Not Configured" warning
 *
 * 2. SENSITIVE DATA DISPLAY
 *    - Account number should already be masked from API
 *    - Don't log to console
 *    - Display as-is from bankDetails prop
 *
 * 3. PAYOUT SETTINGS
 *    - Show schedule (weekly on X day)
 *    - Show minimum threshold
 *    - Show vendor share percentage
 *    - All non-editable (read-only display)
 *
 * ============================================================================
 */

import React from 'react';
import { CreditCard } from 'lucide-react';
import { formatCurrency } from '../../utils/revenueFormatters';
import type { UserBankDetails } from '../../types/revenue';

interface BankAccountCardProps {
  bankDetails: UserBankDetails | undefined;
  vendorSharePercentage: number;
  payoutDay: string;
  minimumThreshold: number;
}

/**
 * Bank account and payout settings card
 */
export function BankAccountCard({
  bankDetails,
  vendorSharePercentage,
  payoutDay,
  minimumThreshold,
}: BankAccountCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Payout Settings</h3>
      </div>

      {bankDetails?.bank_name ? (
        // Has bank details - Show active state
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Split Payments</p>
                <p className="text-xs text-gray-600">Active</p>
              </div>
            </div>
            <span className="px-2 py-1 bg-green-600 text-white rounded-lg text-xs font-bold">
              Active
            </span>
          </div>

          {/* Bank Details */}
          <div className="pt-3 border-t border-green-200 text-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-normal">Bank:</span>
              <span className="font-semibold text-gray-900">{bankDetails.bank_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-normal">Account Number:</span>
              <span className="font-mono text-gray-900 font-semibold">
                {bankDetails.bank_account_number_masked || 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-normal">Account Name:</span>
              <span className="font-semibold text-gray-900">
                {bankDetails.bank_account_name || 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-green-200">
              <span className="text-gray-600 font-normal">Your Share:</span>
              <span className="font-bold text-gray-900">{vendorSharePercentage}%</span>
            </div>
          </div>
        </div>
      ) : (
        // No bank details - Show warning
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
          <p className="text-sm font-semibold text-yellow-900">Split Payments Not Configured</p>
          <p className="text-xs text-yellow-700 mt-1 font-normal">
            Add your bank details and contact admin to set up automatic payouts
          </p>
        </div>
      )}

      {/* Payout Schedule Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Payout Schedule</p>
          <p className="font-bold text-gray-900 text-sm">
            Weekly ({payoutDay}s)
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Minimum Payout</p>
          <p className="font-bold text-gray-900 text-sm">{formatCurrency(minimumThreshold)}</p>
        </div>
      </div>
    </div>
  );
}
